'use client';

import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';

import { Button } from '@/components/core/button';
import { cn } from '@/shared/helpers';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

function DialogContent({
  className,
  children,
  side,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  side?: 'left' | 'right';
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-side={side}
        className={cn(
          // data-closed:pointer-events-none - a dialog animating out should ignore clicks
          'fixed z-50 flex w-full flex-col gap-5 bg-background px-5 py-4 text-sm text-popover-foreground ring-1 ring-foreground/10 outline-none data-closed:pointer-events-none',
          side
            ? 'inset-0 h-svh max-h-none max-w-none rounded-none duration-200 ease-in-out sm:inset-y-4 sm:h-[calc(100svh-2rem)] sm:max-w-md sm:rounded-xl data-[side=left]:sm:right-auto data-[side=left]:sm:left-4 data-[side=right]:sm:right-4 data-[side=right]:sm:left-auto data-open:animate-in data-open:fade-in-0 data-[side=left]:data-open:slide-in-from-left-10 data-[side=right]:data-open:slide-in-from-right-10 data-closed:animate-out data-closed:fade-out-0 data-[side=left]:data-closed:slide-out-to-left-10 data-[side=right]:data-closed:slide-out-to-right-10'
            : 'top-1/2 left-1/2 max-h-[calc(100svh-2rem)] max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl duration-100 sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute top-3 right-2.5 in-[&:has([data-slot=dialog-header])]:top-3.5"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        '-mx-5 -mt-4 grid gap-1 rounded-t-xl px-5 not-last:border-b',
        'h-14 grid-rows-[1fr] items-center',
        'has-data-[slot=dialog-description]:h-auto has-data-[slot=dialog-description]:grid-rows-[auto_auto] has-data-[slot=dialog-description]:items-start has-data-[slot=dialog-description]:py-4',
        className,
      )}
      {...props}
    />
  );
}

const DialogBody = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="dialog-body"
      // using overflow clips focus ring outlines in the content, using negative margin fixes this
      className={cn(
        '-m-2 flex flex-1 flex-col gap-4 overflow-auto p-2 text-sm',
        'in-[&:has([data-slot=dialog-header])]:-mt-5 in-[&:has([data-slot=dialog-header])]:pt-5',
        'in-[&:has([data-slot=dialog-footer])]:-mb-5 in-[&:has([data-slot=dialog-footer])]:pb-5',
        className,
      )}
      {...props}
    />
  ),
);
DialogBody.displayName = 'DialogBody';

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        '-mx-5 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-background px-5 py-4 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-heading text-base leading-snug font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
